<script setup>
import { lohnletterList } from '@/client'
import { toUnderscore, useTextStore } from '@/stores/texts'
import { client } from '@/lohnrechner'
import { inject } from 'vue'
import { ref } from 'vue'

const letters = ref([])
const categories = ref([])
const categorySlugs = []
const branchen = ref([])
const branchenIds = ref([])

const filter = ref(null)
const filterOpen = ref(false)

const brancheFilter = ref(null)

const languageStore = useTextStore()

let language = null

function fetchEntries() {
  lohnletterList({
    client: client,
    query: { language: toUnderscore(languageStore.language) }
  }).then((response) => {
    language = languageStore.language
    letters.value = []
    categories.value = []
    categorySlugs.length = 0
    branchen.value = []
    branchenIds.value = []

    response.data.results.forEach((entry) => {
      entry.categories.forEach((category) => {
        if (categorySlugs.includes(category.slug)) return
        categories.value.push(category)
        categorySlugs.push(category.slug)
      })
      entry.branchen.forEach((branche) => {
        if (branchenIds.value.includes(branche.pk)) return
        branchenIds.value.push(branche.pk)
        branchen.value.push(branche)
      })

      letters.value.push(entry)
    })
  })
}

fetchEntries()

languageStore.$subscribe((mutation, state) => {
  if (language !== state.language) {
    fetchEntries()
  }
})

function filterEntries(category) {
  if (filter.value === category.slug) {
    clearFilter()
  } else {
    filter.value = category.slug
  }
}

function filterCategory(entry) {
  if (!filter.value && !brancheFilter.value) return true
  if (entry.categories.find((category) => category.slug === filter.value)) return true
  if (entry.branchen.find((branche) => branche.pk === brancheFilter.value)) return true
  return false
}

function clearFilter() {
  filter.value = null
}
const t = inject('t')
</script>

<template>
  <div class="buttons">
    <button
      v-for="category in categories"
      @click="filterEntries(category)"
      :class="{ active: filter === category.slug }"
      :key="category.slug"
    >
      {{ category.name }}
    </button>
  </div>
  <div class="filterWrapper">
    <div
      class="filter"
      :class="{ open: filterOpen }"
      @click="filterOpen = !filterOpen"
      v-if="branchen.length"
    >
      <span>
        {{ t('nach-branche-filtern') }}
        <svg
          width="18"
          height="12"
          viewBox="0 0 18 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16.8418 1.67773L8.81748 9.70226L0.792968 1.67775"
            stroke="#e2001a"
            stroke-width="2.22581"
          />
        </svg>
      </span>
      <div v-if="filterOpen" class="branchenContainer">
        <div
          v-for="branche in branchen"
          :key="branche.pk"
          class="branche"
          :class="{ active: brancheFilter === branche.pk }"
          @click="
            brancheFilter === branche.pk ? (brancheFilter = null) : (brancheFilter = branche.pk)
          "
        >
          {{ branche.name }}
        </div>
      </div>
    </div>
  </div>
  <div class="letters">
    <section v-for="entry in letters" :key="entry.slug" :class="{ hidden: !filterCategory(entry) }">
      <img :src="entry.image_preview" alt="" />
      <RouterLink :to="{ name: 'lohnletter-detail', params: { slug: entry.slug } }">
        <span v-for="category in entry.categories" :key="category.slug">{{ category.name }}</span>
        <div>
          <h2>{{ entry.title }}</h2>
        </div>
      </RouterLink>
    </section>
  </div>
</template>

<style scoped>
.filter span {
  display: flex;
  gap: 20px;
  align-items: center;
}

.filter {
  padding: 33px 20px 0px 20px;
  margin-top: -20px;
  border-radius: 20px;
  font-size: 17px;
  color: var(--color-signal);
  font-weight: 700;
  gap: 20px;
  cursor: pointer;
  align-self: flex-start;
  z-index: 100;
  background-color: white;
  border: solid 2px white;
}

.filterWrapper {
  max-height: 52px;
  height: 52px;
  z-index: 100;
  display: flex;
}

.filter.open {
  border: solid 2px var(--color-signal);
  padding-bottom: 20px;
}

.filter svg {
  transition: all 200ms;
}

.branchenContainer {
  padding-top: 20px;
}

.branche {
  padding-top: 10px;
  padding-left: 20px;
  margin-left: -20px;
  padding-right: 20px;
  margin-right: -20px;
  padding-bottom: 10px;
  font-size: 17px;
  color: var(--color-signal);
  font-weight: 400;
  cursor: pointer;
  transition: all 200ms;
}

.branche.active {
  font-weight: 700;
  background-color: var(--color-signal);
  color: white;
}

.branche.active:hover {
  background-color: #e2001abf;
}

.branche:hover {
  background-color: var(--color-grey);
}

.filter.open svg {
  transform: scaleY(-1);
}

h1 {
  margin-bottom: 80px;
}

.buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 20px;
}

.hidden {
  display: none;
}

button {
  border: solid 1px var(--color-signal);
  background-color: white;
  color: var(--color-signal);
  border-radius: 30px;
  height: 40px;
  width: 200px;
  font-weight: 700;
  transition: all 200ms;
  cursor: pointer;
  font-size: 17px;
}

button.active {
  background-color: var(--color-signal);
  color: white;
}

.letters {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  max-width: 100%;
  margin-bottom: 3rem;
}

@media screen and (max-width: 768px) {
  .letters {
    grid-template-columns: 1fr;
  }
}

section {
  overflow: hidden;
  background: #f9f1f1;
  border-radius: 30px;
  position: relative;
  aspect-ratio: 3.2 / 5.1;
  transition: all 200ms;
  cursor: pointer;
}

section:hover {
  transform: scale(var(--hover-scale));
}

.letters section img {
  position: absolute;
  top: 31px;
  bottom: 31px;
  left: 17px;
  right: 17px;
  width: calc(100% - 34px);
  border-radius: 30px;
}

.letters section a {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  padding-left: 30px;
  padding-bottom: 60px;
  padding-right: 30px;
  text-decoration: none;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.letters h2 {
  text-align: left;
  display: inline;
  margin: 0;
  background-image: linear-gradient(
    to top,
    transparent 15%,
    rgb(255, 255, 255) 15%,
    white 90%,
    transparent 90%
  );
  line-height: 1.16;
  font-size: calc(1.5 * var(--font-size));
  letter-spacing: 2px;
  box-decoration-break: clone;
  padding-left: 4px;
  padding-left: 4px;
}

.letters section span {
  background-color: var(--color-signal);
  display: inline-block;
  align-self: flex-start;
  padding: 4px 15px;
  border-radius: 23px;
  color: white;
  margin-bottom: 15px;
  font-weight: 700;
  font-size: calc(0.8 * var(--font-size));
}
</style>
